import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';

import { Button, FormModal, HooksService, Label, Switch } from 'basic-components';
import { DataLakeQuery } from 'dashboard-services/api/dataLake';
import Query from 'dashboard-services/api/Query';
import { CommonService } from 'dashboard-services/services';
import { CodeMirrorWrapper, getCodeMirrorLanguage, globalActions, statusBoxActions } from 'primary-components';

import { codeEditorActions } from 'actions/modules/tools';
import getApiConfig from 'api/ApiConfig';
import { filesDatalakeQueries } from 'queries/modules/datalake';
import { sendEvent } from 'services/GAService';

import CodeTileService from 'components/modules/Insight/CustomInsight/ChartInsight/tiles/CodeTile/CodeTileService';

import FileDetails from 'basic/FileDetails';

import { useQueryClient } from '@tanstack/react-query';

import classNames from 'classnames';
import './CodeEditor.scss';

const ctrlKey = 17, rightCmdKey = 93, leftCmdKey = 91, sKey = 83, yKey = 89
const CodeEditor = () => {
  const isMounted = HooksService.useIsMounted()
  const dispatch = useDispatch(),
        { id: fileId, editable, defaultValue, valueMode, valueCallback, valueModeType, newFile, extension, groupName: groupNameLoc, provider, onSubmitCallback } = useSelector(state => state.toolsState.codeEditorState),
        [ctrlDown, setCtrlDown] = useState(false),
        [content, setContent] = useState(valueMode ? JSON.stringify(defaultValue, null, 2) : undefined),
        [fileInfo, setFileInfo] = useState({}),
        [language, setLanguage] = useState({}),
        [lineWrap, setLineWrap] = useState(false),
        [isValidJson, setIsValidJson] = useState(true),
        [errors, setErrors] = useState([]),
        [areFileDetailsEditable, setAreFileDetailsEditable] = useState(newFile),
        formRef = useRef(),
        reqId = useMemo(() => CommonService.generateUID(), []),
        getQueryBuilder = useCallback(() =>
          new Query.Builder(dispatch(getApiConfig()))
            .withOnParse(response => Promise.resolve(response))
            .withHeader("Target", provider, true)
        , [dispatch, provider]),
        getQueryBuilderS3 = useCallback(() => 
          new Query.Builder(dispatch(getApiConfig()))
            .withOnParse(response => Promise.resolve(response))
        , [dispatch]),
        { mutateAsync: getFileLocationCall } = filesDatalakeQueries.useGetFileDefinitionMutation({ getQueryBuilder, id: fileId }),
        { mutateAsync: s3GetCall } = filesDatalakeQueries.useGetFileContentMutation({ getQueryBuilder: getQueryBuilderS3 }),
        isPending = useRef(false),
        swapFetching = useCallback((reqId, isResponse) => {
          dispatch(globalActions.handleRequest({ id: reqId, isResponse }))
          isPending.current = !isResponse
        }, [dispatch]),
        close = useCallback(() => {
          batch(() => {
            dispatch(codeEditorActions.changeValue(undefined, 'id'))
            dispatch(codeEditorActions.changeValue(false, 'open'))
            dispatch(codeEditorActions.changeValue(true, 'editable'))
            dispatch(codeEditorActions.changeValue(false, 'newFile'))
            dispatch(codeEditorActions.changeValue(undefined, 'extension'))
            dispatch(codeEditorActions.changeValue(undefined, 'defaultValue'))
            dispatch(codeEditorActions.changeValue(false, 'valueMode'))
            dispatch(codeEditorActions.changeValue(undefined, 'valueModeType'))
            dispatch(codeEditorActions.changeValue(undefined, 'valueCallback'))
            dispatch(codeEditorActions.changeValue(undefined, 'provider'))
            dispatch(codeEditorActions.changeValue(undefined, 'onSubmitCallback'))
          })
        }, [dispatch]),
        timeoutRef = useRef(),
        downloadFile = useCallback(() => {
          if(valueMode) {
            return Promise.resolve();
          }
          if(newFile) {
            clearTimeout(timeoutRef.current)
            timeoutRef.current = setTimeout(() => setContent(""), 100)
            return Promise.resolve();
          }
          swapFetching(reqId, false)
          let localFileName = ""
          const getMetadataRequest = 
            getFileLocationCall()
                .then(response => {
                  const location = response.headers.get("Location")
                  return response.json().then(r => {
                    setFileInfo(r)
                    dispatch(codeEditorActions.changeValue(r.fileName?.split(".").slice(-1)[0], 'extension'))
                    localFileName = r.fileName
                    return location
                  })
                })
      
          const getFileCall = location => {
            const parsedFileName = String(localFileName).toLowerCase()
            return s3GetCall({ url: location })
                .then(response =>
                  parsedFileName.endsWith(".json") || parsedFileName.endsWith(".ipynb") || !parsedFileName.includes(".") ? 
                    response.text().then(content => {
                      try {
                        return { response: JSON.parse(content), isValid: true }
                      } catch (e) {
                        dispatch(globalActions.getMessageStore()).error("There is an error in the file syntax. " + e.message)
                      }
                      return { response: content, isValid: false }
                    })
                    : 
                    response.text().then(response => ({ response, isValid: true })) 
                )
                .then(({ response, isValid }) => {
                  if(isMounted.current) {
                    setContent(isValid && (parsedFileName.endsWith(".json") || parsedFileName.endsWith(".ipynb") || !parsedFileName.includes(".")) ? JSON.stringify(response, null, 2) : response)
                  }
                  if(parsedFileName.endsWith(".json")) {
                    setIsValidJson(isValid)
                  }
                })
                .finally(() => swapFetching(reqId, true))
          }
          return getMetadataRequest.then(getFileCall)
        }, [valueMode, newFile, swapFetching, reqId, getFileLocationCall, dispatch, s3GetCall, isMounted]),
        onChange = useCallback(content => setContent(content), []),
        queryClient = useQueryClient(),
        refreshTimeoutRef = useRef(),
        fileFields = useMemo(() => Object.entries(fileInfo?.fields || {}).map(([k, v]) => ({ name: k, value: v })), [fileInfo]),
        getQueryBuilderUpload = useCallback(() => 
          new Query.Builder(dispatch(getApiConfig()))
            .withOnParse(response => Promise.resolve(response))
            .withOnError(({ response = {}, message }) => {
              setErrors(response.validationErrors)
              if(message) {
                dispatch(globalActions.getMessageStore()).error(message)
              }
            })
        , [dispatch]),
        { uploadFileMetadata, uploadFileContent } = filesDatalakeQueries.useFileUpload({ getQueryBuilder: getQueryBuilderUpload, fileName: fileInfo?.fileName, groupName: fileInfo?.groupName, fileType: fileInfo?.fileType, fields: fileFields }),
        onSubmit = useCallback(() => {
          if(isPending.current) {
            return Promise.resolve()
          }
          if(!editable) {
            return Promise.resolve();
          }
          if(valueMode) {
            valueCallback(CommonService.parseJson(content, content))
            close()
            return Promise.resolve()
          }
          if(areFileDetailsEditable) {
            formRef.current?.validateForForm()
            if(!formRef.current?.isValid()) {
              return Promise.resolve();
            }
          }
          const reqId = CommonService.generateUID()
          dispatch(globalActions.handleRequest({ id: reqId }))
          return uploadFileMetadata()
            .then(response => {
              const jobId = response.headers.get("X-KH-JOB-ID")
              dispatch(statusBoxActions.addJob({ jobId, displayName: fileInfo.fileName, subtitle: fileInfo.groupName }))
              return response
            })
            .then(response => response.json())
            .then(response => uploadFileContent({
              url: response.location,
              image: content
            })
            .then(() => dispatch(globalActions.getMessageStore()).success(newFile ? "New file has been saved." : "New version of file has been saved."))
            .then(() => {
              close()
              clearTimeout(refreshTimeoutRef.current)
              refreshTimeoutRef.current = setTimeout(() => queryClient.invalidateQueries({ queryKey: DataLakeQuery.KEYS.SEARCH_FILES() }), 3000)
              onSubmitCallback?.({ name: fileInfo?.fileName, groupName: fileInfo?.groupName })
            })
            .finally(() => dispatch(globalActions.handleRequest({ id: reqId, isResponse: true })))
          )
        }, [editable, valueMode, areFileDetailsEditable, dispatch, uploadFileMetadata, valueCallback, content, close, fileInfo, uploadFileContent, newFile, queryClient, onSubmitCallback]),
        isDisabled = useMemo(() => valueMode ? false : !editable, [editable, valueMode]),
        submitProps = useMemo(() => ({
          disabled: isDisabled
        }), [isDisabled]),
        codeEditor = useRef(),
        onKeyDown = useCallback(e => {
          (e.keyCode === ctrlKey || e.keyCode === leftCmdKey || e.keyCode === rightCmdKey) && setCtrlDown(true)
          if(ctrlDown && e.keyCode === sKey) {
            e.preventDefault()
            onSubmit()
          }
          if(ctrlDown && e.keyCode === yKey) {
            e.preventDefault()
            codeEditor?.current?._redoEdit()
          }
        }, [ctrlDown, onSubmit, codeEditor]),
        onKeyUp = useCallback(e => (e.keyCode === ctrlKey || e.keyCode === leftCmdKey || e.keyCode === rightCmdKey) && setCtrlDown(false), []),
        clearCtrl = useCallback(() => setCtrlDown(false), []),
        onChangeLineWrap = useCallback(() => setLineWrap(lineWrap => !lineWrap), []),
        beautify = useCallback(() => {
          const formattedContent = CodeTileService.beautify({ codeType: extension, content })
          if(formattedContent !== content) {
            codeEditor.current?.setValue(formattedContent)
          }
        }, [content, extension]),
        newFileHeader = useMemo(() => (
          <FileDetails
              areFileDetailsEditable={areFileDetailsEditable}
              className="ng-code-editor__header"
              editable={!isDisabled}
              errors={errors}
              extension={extension}
              fileInfo={fileInfo}
              groupNameLoc={groupNameLoc}
              newFile={newFile}
              ref={formRef}
              setAreFileDetailsEditable={setAreFileDetailsEditable}
              setFileInfo={setFileInfo}
          />
        ), [errors, extension, fileInfo, newFile, isDisabled, groupNameLoc, areFileDetailsEditable])

  useEffect(() => {
    downloadFile(fileId)
  }, [downloadFile, fileId, newFile, valueMode])

  useEffect(() => {
    setLanguage(getCodeMirrorLanguage(extension))
  }, [fileInfo, valueMode, valueModeType, extension])

  useEffect(() => () => {
    swapFetching(reqId, true)
  }, [swapFetching, reqId])

  useEffect(() => {
    const event = {
      category: "view_item",
      action: "code_editor",
      label: "tool",
    };
    dispatch(sendEvent(event))
  }, [dispatch])

  useEffect(() => () => {
    clearTimeout(timeoutRef.current)
  }, [])
  
  return (
    <FormModal
        className={classNames(
          "ng-code-editor line-numbers",
          { "ng-code-editor--value-mode": valueMode }
        )}
        closeOnSubmit={false}
        onBlur={clearCtrl}
        onClose={close}
        onKeyDown={onKeyDown}
        onKeyUp={onKeyUp}
        onSubmit={onSubmit}
        open={content !== undefined}
        size="large"// we still override this in scss
        sizeWidth="large" // we still override this scss
        submitProps={submitProps}
        title={valueMode ? "Code Editor" : newFileHeader}
    >
      <div className="ng-code-editor__controls">
        {extension === "xml" && (
          <Button
              onClick={beautify}
              size="small"
              type="button"
              variant="text"
          >
            Beautify
          </Button>
        )}
        <Label 
            noError
            size="small"
        >
          Wrap text
        </Label>
        <Switch
            on={lineWrap}
            onChange={onChangeLineWrap}
        />
      </div>
      <CodeMirrorWrapper
          className={classNames(
            "ng-code-editor__content",
            { "ng-code-editor__content--is-not-valid": !isValidJson },
          )}
          lineWrapping={lineWrap}
          onChange={onChange}
          options={language}
          ref={codeEditor}
          value={content}
      />
    </FormModal>
  )
}

export default memo(CodeEditor);